import React, { useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import {
  useHomeownerImport,
} from 'src/pages/Property/HomeOwnerImportDialogue/UseHomeownerImporter/useHomeownerImport';
import GenericDialogue from 'src/components/Dialogue/Dialogue';
import DialogueBody from 'src/components/GenericDialogueBody/GenericDialogueBody';
import DialogueFooter from 'src/components/GenericDialogueFooter/GenericDialogueFooter';
import RowValidationErrorCard from 'src/pages/Property/HomeOwnerImportDialogue/Components/RowValidationCard/RowValidationCard';
import PreviewTable, {
  ColumnConfig,
} from 'src/pages/Property/HomeOwnerImportDialogue/Components/PreviewTable/PreviewTable';
import { ISuite } from 'src/components/SharedTypes';
import { REQUIRED_COLUMNS } from 'src/pages/Property/HomeOwnerImportDialogue/UseHomeownerImporter/useHomeownerImport';
import { HomeOwnerImportModel } from 'src/pages/Property/HomeOwnerImportDialogue/UseHomeownerImporter/types';
import CsvTemplateDownloadCard from 'src/components/CsvTemplateDownloadCard/CsvTemplateDownloadCard';
import "./HomeownerImportDialogue.scss";

const homeownerColumns: ColumnConfig<HomeOwnerImportModel>[] = [
  { key: 'FirstName', header: 'First' },
  { key: 'LastName', header: 'Last' },
  { key: 'Email', header: 'Email' },
  { key: 'Phone', header: 'Phone' },
  { key: 'Role', header: 'Role' },
  { key: 'PropertyName', header: 'Property' },
  { key: 'Building', header: 'Building' },
  { key: 'UnitNumber', header: 'Unit' },
  { key: 'IsPrimary', header: 'Primary', render: (v) => (v ? 'Yes' : 'No') },
  { key: 'DashboardOnly', header: 'Dash Only', render: (v) => (v ? 'Yes' : 'No') },
  { key: 'IsDesignated', header: 'Designate', render: (v) => (v ? 'Yes' : 'No') },
];

interface HomeownerImportDialogueProps {
  isOpen: boolean;
  onClose: () => void;
  onImportComplete: () => void;
  filteredSuiteData: ISuite[];
}

const HomeownerImportDialogue = ({
  isOpen,
  onClose,
  onImportComplete,
  filteredSuiteData,
}: HomeownerImportDialogueProps) => {
  const {
    file,
    validationStatus,
    columnValidation,
    parsedRecords,
    importStatus,
    importResult,
    apiError,
    rowValidationErrors,
    processFile,
    runImport,
    reset,
  } = useHomeownerImport(filteredSuiteData);

  const { getRootProps, getInputProps, open } = useDropzone({
    noClick: true,
    accept: '.csv',
    multiple: false,
    onDrop: (acceptedFiles: File[]) => {
      if (acceptedFiles[0]) {
        processFile(acceptedFiles[0]);
      }
    },
  });

  const handleClose = useCallback(() => {
    reset();
    onClose();
  }, [reset, onClose]);

  const handleImportComplete = useCallback(() => {
    reset();
    onImportComplete();
    onClose();
  }, [reset, onImportComplete, onClose]);

  const isImporting: boolean = importStatus === 'importing';
  const isSuccess: boolean = importStatus === 'success';
  const isImportError: boolean = importStatus === 'error';
  const isValidCsv: boolean = validationStatus === 'valid';
  const isImportEnabled: boolean = isValidCsv && !isImporting;

  return (
    <GenericDialogue
      isOpen={isOpen}
      onClose={handleClose}
      title="Import Users"
      description="Upload a CSV to bulk-assign homeowners, occupants, or realtors to units"
      maxWidth="1100px"
      footer={
        <DialogueFooter
          isSuccess={isSuccess}
          isImporting={isImporting}
          file={file}
          isValidCsv={isValidCsv}
          isImportError={isImportError}
          isImportEnabled={isImportEnabled}
          importButtonLabel="Import Users"
          reset={reset}
          handleImportComplete={handleImportComplete}
          handleClose={handleClose}
          runImport={runImport}
        />
      }
    >
      <div className="hid_body">
        {!file && !isSuccess && (
          <CsvTemplateDownloadCard
            filename="user-import-template.csv"
            columns={REQUIRED_COLUMNS}
          />
        )}
        <DialogueBody
          isSuccess={isSuccess}
          isImporting={isImporting}
          file={file}
          importResult={importResult}
          recordCount={parsedRecords.length}
          validationStatus={validationStatus}
          columnValidation={columnValidation}
          hasRowErrors={rowValidationErrors.length > 0}
          isImportError={isImportError}
          apiError={apiError}
          entityName="user"
          successMessage="processed and linked to their units."
          getRootProps={getRootProps}
          getInputProps={getInputProps}
          open={open}
          reset={reset}
          requiredColumns={REQUIRED_COLUMNS}
        >
          {rowValidationErrors.length > 0 && (
            <RowValidationErrorCard errors={rowValidationErrors} />
          )}
          {validationStatus === 'valid' && (
            <PreviewTable records={parsedRecords} columns={homeownerColumns} />
          )}
        </DialogueBody>
      </div>
    </GenericDialogue>
  );
};

export default HomeownerImportDialogue;